import { TenantsAdminService } from '@admin/admin-tenants/services/tenants.service';
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { AuthStore } from '../stores/auth.store';
import { legacyTenantRedirect, resolveTenantAccess } from '../tenant/tenant-access';
import { TenantContextStore } from '../tenant/tenant-context.store';
import { LOGIN_ROUTE } from './auth-route.constants';

/** Guards `/tenant/:tenantId`: staff may open any existing tenant, everyone else only their own. */
export const tenantAccessGuard: CanActivateFn = (route) => {
  const auth = inject(AuthStore);
  const router = inject(Router);
  const tenants = inject(TenantsAdminService);
  const tenantContext = inject(TenantContextStore);
  const tenantId = route.paramMap.get('tenantId')?.toLowerCase() ?? '';

  const enter = (): boolean => {
    tenantContext.setTenantId(tenantId);
    return true;
  };

  return auth.waitUntilSessionReady().pipe(
    switchMap(() => {
      const user = auth.user();
      if (!auth.isAuthenticated() || !user) {
        return of(router.createUrlTree([LOGIN_ROUTE]));
      }

      const access = resolveTenantAccess(user, tenantId);
      switch (access.kind) {
        case 'allow':
          return of(enter());
        case 'redirect':
          return of(router.createUrlTree(access.commands));
        case 'staff':
          // The API answers 200 with an empty body for an unknown or deleted tenant.
          return tenants.findOne(tenantId).pipe(
            map((tenant) => (tenant ? enter() : router.createUrlTree(['/admin/tenants']))),
            catchError(() => of(router.createUrlTree(['/admin/tenants']))),
          );
      }
    }),
  );
};

/** Sends tenant-less URLs (`/tenant`, `/tenant/settings/users`, ...) to their `/tenant/:tenantId` equivalent. */
export const tenantLegacyRedirectGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthStore);
  const router = inject(Router);

  return auth.waitUntilSessionReady().pipe(
    map(() => {
      const user = auth.user();
      if (!auth.isAuthenticated() || !user) {
        return router.createUrlTree([LOGIN_ROUTE]);
      }
      return router.parseUrl(legacyTenantRedirect(user, state.url.replace(/^\/tenant/, '')));
    }),
  );
};
